import React from 'react';
import { createPortal } from 'react-dom';
import { X, User, Mail, Phone, Calendar, Shield, CheckCircle, XCircle, Clock, Square, MapPin, Globe, Building } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

function UserDetailsPopup({ user, isOpen, onClose, theme, currentThemeName }) {
  const { t } = useLanguage();
  if (!isOpen || !user) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    if (!status) return t('userDetails.unknown');
    switch (status) {
      case 'active': return t('userDetails.statusActive');
      case 'blocked': return t('userDetails.statusBlocked');
      case 'pending': return t('userDetails.statusPending');
      case 'stopped': return t('userDetails.statusStopped');
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle size={16} color="#10b981" />;
      case 'blocked': return <XCircle size={16} color="#ef4444" />;
      case 'pending': return <Clock size={16} color="#f59e0b" />;
      case 'stopped': return <Square size={16} color="#6b7280" />;
      default: return <Square size={16} color="#6b7280" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'blocked': return '#ef4444';
      case 'pending': return '#f59e0b';
      case 'stopped': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const isDark = currentThemeName === 'dark';

  const overlay = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-details-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: '24px',
        boxSizing: 'border-box',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: isDark ? '#1e293b' : '#ffffff',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '440px',
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: isDark ? '0 25px 50px -12px rgba(0,0,0,0.5)' : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: currentThemeName === 'dark' ? '1px solid #374151' : '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: currentThemeName === 'dark' ? '#374151' : '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <User size={24} color={currentThemeName === 'dark' ? '#9ca3af' : '#6b7280'} />
            </div>
            <div>
              <h2 id="user-details-title" style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: currentThemeName === 'dark' ? '#f9fafb' : '#111827'
              }}>
                {user.first_name} {user.last_name}
              </h2>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '4px'
              }}>
                {getStatusIcon(user.status)}
                <span style={{
                  fontSize: '14px',
                  color: getStatusColor(user.status),
                  fontWeight: '500'
                }}>
                  {getStatusLabel(user.status)}
                </span>
                {user.is_admin && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 8px',
                    backgroundColor: currentThemeName === 'dark' ? '#1e40af' : '#dbeafe',
                    borderRadius: '12px'
                  }}>
                    <Shield size={12} color="#3b82f6" />
                    <span style={{ fontSize: '12px', color: '#3b82f6', fontWeight: '500' }}>
                      {t('userDetails.admin')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('userDetails.close')}
            style={{
              background: isDark ? 'rgba(255,255,255,0.08)' : '#f3f4f6',
              border: 'none',
              cursor: 'pointer',
              padding: '10px',
              borderRadius: '10px',
              color: isDark ? '#e5e7eb' : '#374151',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.12)' : '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.08)' : '#f3f4f6';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ display: 'grid', gap: '20px' }}>
          {/* Basic Information */}
          <div>
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '16px',
              fontWeight: '600',
              color: currentThemeName === 'dark' ? '#f9fafb' : '#111827'
            }}>
              {t('userDetails.basicInformation')}
            </h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Mail size={16} color={currentThemeName === 'dark' ? '#9ca3af' : '#6b7280'} />
                <div>
                  <div style={{ fontSize: '12px', color: currentThemeName === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '2px' }}>
                    {t('userDetails.email')}
                  </div>
                  <div style={{ fontSize: '14px', color: currentThemeName === 'dark' ? '#f9fafb' : '#111827' }}>
                    {user.email || 'N/A'}
                  </div>
                </div>
              </div>
              
              {user.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Phone size={16} color={currentThemeName === 'dark' ? '#9ca3af' : '#6b7280'} />
                  <div>
                    <div style={{ fontSize: '12px', color: currentThemeName === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '2px' }}>
                      {t('userDetails.phone')}
                    </div>
                    <div style={{ fontSize: '14px', color: currentThemeName === 'dark' ? '#f9fafb' : '#111827' }}>
                      {user.phone}
                    </div>
                  </div>
                </div>
              )}

              {user.language && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Globe size={16} color={currentThemeName === 'dark' ? '#9ca3af' : '#6b7280'} />
                  <div>
                    <div style={{ fontSize: '12px', color: currentThemeName === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '2px' }}>
                      {t('userDetails.language')}
                    </div>
                    <div style={{ fontSize: '14px', color: currentThemeName === 'dark' ? '#f9fafb' : '#111827' }}>
                      {user.language}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Business Information */}
          {(user.business_type || user.website) && (
            <div>
              <h3 style={{
                margin: '0 0 12px 0',
                fontSize: '16px',
                fontWeight: '600',
                color: currentThemeName === 'dark' ? '#f9fafb' : '#111827'
              }}>
                {t('userDetails.businessInformation')}
              </h3>
              <div style={{ display: 'grid', gap: '12px' }}>
                {user.business_type && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Building size={16} color={currentThemeName === 'dark' ? '#9ca3af' : '#6b7280'} />
                    <div>
                      <div style={{ fontSize: '12px', color: currentThemeName === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '2px' }}>
                        {t('userDetails.businessType')}
                      </div>
                      <div style={{ fontSize: '14px', color: currentThemeName === 'dark' ? '#f9fafb' : '#111827' }}>
                        {user.business_type}
                      </div>
                    </div>
                  </div>
                )}

                {user.website && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Globe size={16} color={currentThemeName === 'dark' ? '#9ca3af' : '#6b7280'} />
                    <div>
                      <div style={{ fontSize: '12px', color: currentThemeName === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '2px' }}>
                        {t('userDetails.website')}
                      </div>
                      <div style={{ fontSize: '14px', color: currentThemeName === 'dark' ? '#f9fafb' : '#111827' }}>
                        <a 
                          href={user.website.startsWith('http') ? user.website : `https://${user.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#3b82f6', textDecoration: 'none' }}
                        >
                          {user.website}
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Account Information */}
          <div>
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '16px',
              fontWeight: '600',
              color: currentThemeName === 'dark' ? '#f9fafb' : '#111827'
            }}>
              {t('userDetails.accountInformation')}
            </h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Calendar size={16} color={currentThemeName === 'dark' ? '#9ca3af' : '#6b7280'} />
                <div>
                  <div style={{ fontSize: '12px', color: currentThemeName === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '2px' }}>
                    {t('userDetails.createdAt')}
                  </div>
                  <div style={{ fontSize: '14px', color: currentThemeName === 'dark' ? '#f9fafb' : '#111827' }}>
                    {formatDate(user.created_at)}
                  </div>
                </div>
              </div>

              {user.last_login && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Calendar size={16} color={currentThemeName === 'dark' ? '#9ca3af' : '#6b7280'} />
                  <div>
                    <div style={{ fontSize: '12px', color: currentThemeName === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '2px' }}>
                      {t('userDetails.lastLogin')}
                    </div>
                    <div style={{ fontSize: '14px', color: currentThemeName === 'dark' ? '#f9fafb' : '#111827' }}>
                      {formatDate(user.last_login)}
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Shield size={16} color={currentThemeName === 'dark' ? '#9ca3af' : '#6b7280'} />
                <div>
                  <div style={{ fontSize: '12px', color: currentThemeName === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '2px' }}>
                    {t('userDetails.verificationStatus')}
                  </div>
                  <div style={{ fontSize: '14px', color: currentThemeName === 'dark' ? '#f9fafb' : '#111827' }}>
                    {user.is_verified ? t('userDetails.verified') : t('userDetails.notVerified')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Optional fields only if present */}
          {(user.business_name || user.website || user.address_line1) && (
            <div>
              <h3 style={{
                margin: '0 0 12px 0',
                fontSize: '16px',
                fontWeight: '600',
                color: currentThemeName === 'dark' ? '#f9fafb' : '#111827'
              }}>
                {t('userDetails.moreDetails')}
              </h3>
              <div style={{ display: 'grid', gap: '12px' }}>
                {user.business_name && (
                  <div>
                    <div style={{ fontSize: '12px', color: currentThemeName === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '2px' }}>{t('userDetails.businessName')}</div>
                    <div style={{ fontSize: '14px', color: currentThemeName === 'dark' ? '#f9fafb' : '#111827' }}>{user.business_name}</div>
                  </div>
                )}
                {user.website && (
                  <div>
                    <div style={{ fontSize: '12px', color: currentThemeName === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '2px' }}>{t('userDetails.website')}</div>
                    <a href={user.website.startsWith('http') ? user.website : `https://${user.website}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '14px', color: '#3b82f6' }}>{user.website}</a>
                  </div>
                )}
                {user.address_line1 && (
                  <div>
                    <div style={{ fontSize: '12px', color: currentThemeName === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '2px' }}>{t('userDetails.address')}</div>
                    <div style={{ fontSize: '14px', color: currentThemeName === 'dark' ? '#f9fafb' : '#111827' }}>{[user.address_line1, user.city, user.state, user.zip_code].filter(Boolean).join(', ')}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}

export default UserDetailsPopup;
